import { mulRate, sub, ZERO, type Money } from "../money/money";
import { stripVat, type TaxConfig } from "./price-cart";
import { prorateForCoverage } from "./proration";

/**
 * Senior Citizen / PWD statutory discount computation (FR-028, T172).
 *
 * METHODOLOGY, VERIFIED AGAINST THE PUBLISHED RULE (BIR Revenue Regulations
 * 7-2010, implementing RA 9994 and RA 10754), not assumed:
 *
 *   1. Strip VAT from the eligible (and, for a shared bill, covered-share)
 *      amount to get the VAT-exclusive base.
 *   2. Take 20% of that base — the statutory rate, configured per business as
 *      `business_profile.statutory_discount_bp` (data-model.md), not
 *      hardcoded here, since the Act's rate is the default but the schema
 *      anticipates it could change.
 *   3. The customer owes base minus that 20%. VAT on the covered portion is
 *      exempted outright, not merely reduced.
 *
 * Every fixture in tests/fixtures/pricing/statutory.json reproduces that
 * source's worked examples to the centavo, using this codebase's existing
 * half-away-from-zero rounding (price-cart.ts's `stripVat` and money.ts's
 * `mulRate`) — this is what confirms the two independently-described rules
 * ("strip VAT, then 20%" from the regulation; "half away from zero at the
 * centavo" from this codebase) actually agree once real money is involved.
 *
 * NON-VAT REGIME (research.md R9): no VAT exists to strip, so the rate
 * applies flat to the eligible amount, and nothing is reported as exempted
 * VAT — there is no VAT in this regime to begin with.
 *
 * TAX-EXCLUSIVE PRICING: the eligible amount computed from `PricedLine`
 * totals is already VAT-exclusive under this configuration (price-cart.ts's
 * tax-exclusive branch ADDS tax on top rather than deriving it as a
 * residual), so there is nothing to strip — but the VAT that would otherwise
 * have been added on top is what gets exempted, computed the same way
 * (`mulRate` of the base, not a subtraction).
 */

export interface StatutoryDiscountInput {
  /** The statutory-eligible amount for this discount, before proration. */
  eligibleAmount: Money;
  coveredCustomers: number;
  totalCustomers: number;
  tax: TaxConfig;
  /** Basis points, e.g. 2000 = 20%. From `business_profile.statutory_discount_bp`. */
  statutoryRateBp: number;
}

export interface StatutoryDiscountResult {
  /** The eligible amount actually covered, after shared-bill proration. */
  coveredAmount: Money;
  /** Left untouched — taxed and priced normally (FR-030's remainder). */
  uncoveredAmount: Money;
  /** The VAT-exclusive base the 20% is taken from. */
  vatExclusiveBase: Money;
  /** What the covered amount is reduced by. */
  discountAmount: Money;
  /** vatExclusiveBase − discountAmount: what the covered customers owe. */
  amountDue: Money;
  /** VAT exempted from the covered portion. Zero under a non-VAT regime. */
  exemptedVat: Money;
}

export function computeStatutoryDiscount(input: StatutoryDiscountInput): StatutoryDiscountResult {
  const { eligibleAmount, coveredCustomers, totalCustomers, tax, statutoryRateBp } = input;

  const { coveredShare, uncoveredShare } = prorateForCoverage(
    eligibleAmount,
    coveredCustomers,
    totalCustomers,
  );

  if (coveredShare === ZERO) {
    return {
      coveredAmount: ZERO,
      uncoveredAmount: uncoveredShare,
      vatExclusiveBase: ZERO,
      discountAmount: ZERO,
      amountDue: ZERO,
      exemptedVat: ZERO,
    };
  }

  const isVat = tax.regime === "vat";
  const vatExclusiveBase =
    isVat && tax.pricesTaxInclusive ? stripVat(coveredShare, tax.rateBp) : coveredShare;

  const exemptedVat = !isVat
    ? ZERO
    : tax.pricesTaxInclusive
      ? sub(coveredShare, vatExclusiveBase)
      : mulRate(vatExclusiveBase, tax.rateBp);

  const discountAmount = mulRate(vatExclusiveBase, statutoryRateBp);
  const amountDue = sub(vatExclusiveBase, discountAmount);

  return {
    coveredAmount: coveredShare,
    uncoveredAmount: uncoveredShare,
    vatExclusiveBase,
    discountAmount,
    amountDue,
    exemptedVat,
  };
}
